import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Reservation } from '../../core/model/reservation';
import { isPast, isUpcoming } from '../../core/model/reservation';
import { Formatters } from '../../core/util/formatters';
import { nowDateTime } from '../../core/util/datetime';
import { S } from '../../i18n/strings';
import { useContainer } from '../../state/ContainerContext';
import { useSession } from '../../state/SessionContext';
import { UiStates } from '../../state/uiState';
import { useAsync } from '../../state/useAsync';
import { Photo } from '../components/Photo';
import { EmptyState, StateContainer } from '../components/StateContainer';
import { statusLabel } from './ReservationDetailScreen';

type Tab = 'UPCOMING' | 'PAST' | 'CANCELLED';

const TABS: { key: Tab; label: string; empty: string }[] = [
  { key: 'UPCOMING', label: S.reservationsTabUpcoming, empty: S.reservationsEmptyUpcoming },
  { key: 'PAST', label: S.reservationsTabPast, empty: S.reservationsEmptyPast },
  { key: 'CANCELLED', label: S.reservationsTabCancelled, empty: S.reservationsEmptyCancelled },
];

export function ReservationsScreen() {
  const navigate = useNavigate();
  const { reservations } = useContainer();
  const { session } = useSession();
  const [tab, setTab] = useState<Tab>('UPCOMING');

  const { state, reload } = useAsync<Reservation[], readonly Reservation[]>(
    () => reservations.getAll(),
    [reservations],
    (items) => UiStates.success(items),
  );

  const now = useMemo(() => nowDateTime(), []);

  if (session.kind !== 'AUTHENTICATED') {
    return (
      <EmptyState
        icon="🔐"
        title={S.profileGuestTitle}
        body={S.profileGuestBody}
        action={{ label: S.authLogin, onClick: () => navigate('/entrar?volver=/reservas') }}
      />
    );
  }

  return (
    <div className="stack">
      <h1>{S.reservationsTitle}</h1>

      <div className="tabs" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            className="tabs__tab"
            aria-selected={tab === entry.key}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <StateContainer state={state} onRetry={reload}>
        {(all) => {
          const filtered = all.filter((reservation) => {
            switch (tab) {
              case 'UPCOMING':
                return isUpcoming(reservation, now);
              case 'PAST':
                return isPast(reservation, now);
              case 'CANCELLED':
                return reservation.status === 'CANCELLED';
            }
          });

          if (filtered.length === 0) {
            return (
              <EmptyState
                icon="📅"
                title={TABS.find((t) => t.key === tab)!.empty}
                action={{ label: S.reservationsEmptyAction, onClick: () => navigate('/buscar') }}
              />
            );
          }

          return (
            <div className="stack stack--tight">
              {filtered.map((reservation) => (
                <button
                  key={reservation.id}
                  type="button"
                  className="reservation-card"
                  onClick={() => navigate(`/reservas/${reservation.id}`)}
                >
                  <Photo
                    className="reservation-card__photo"
                    src={reservation.restaurantPhoto}
                    alt=""
                  />
                  <div className="reservation-card__body">
                    <strong>{reservation.restaurantName}</strong>
                    <span className="muted">
                      {Formatters.longDate(reservation.date)} · {Formatters.time(reservation.time)} ·{' '}
                      {Formatters.partySize(reservation.partySize)}
                    </span>
                    <span className="muted">{S.confirmationCode(reservation.confirmationCode)}</span>
                    <span className={`status-chip status-chip--${reservation.status.toLowerCase()}`}>
                      {statusLabel(reservation.status)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          );
        }}
      </StateContainer>
    </div>
  );
}
